import React, {useContext} from 'react';
import {FormattedMessage} from "react-intl";
import {Box, Table, TableBody, TableCell, TableHead, TableRow} from "@mui/material";

import TableHeader from "./components/TableHeader";
import OrderRow from "./components/OrderRow";
import OrderRowsSkeleton from "./components/OrderRowsSkeleton";

import Pagination from "components/WMS/List/Pagination";
import ListingEmptyState from "components/Product/Table/ListingEmptyState";

import useOrderList from "./useOrderList";

import {OrderListContext} from "../../context/OrderListContext";

import {greyishBlue100} from "assets/jss/main";

import {sessionStore} from "utils/sessionStore";
import {Authorizations, hasAuthorization} from "utils/authorizations";

function OrderTable() {
    const {retrieved, loading, pageCount, handlePageChange, currentPage, paginationKey} = useOrderList();

    const {queryParams, setQueryParams, search, setSearch, selectedFilters, setSelectedFilters} = useContext(OrderListContext);

    const hasActiveSearchOrFilters = search.trim().length > 0 || selectedFilters.length > 0;

    function handleResetSearchAndFilters() {
        setSearch('');
        setSelectedFilters([]);
    }

    // retailers never see who an order is assigned to, mirroring the
    // tableHeadData condition in src/views/picking/order/list.js
    const isRetailer = hasAuthorization(sessionStore.get('authorizations'), Authorizations.PICKING_RETAILER);

    const sortDirection = new URLSearchParams(queryParams).get('order[createdAt]') === 'desc' ? 'desc' : 'asc';

    function handleSortByCreatedAt() {
        const nextDirection = sortDirection === 'asc' ? 'desc' : 'asc';

        setQueryParams((previousQueryParams) => {
            const params = new URLSearchParams(previousQueryParams);

            params.set('order[createdAt]', nextDirection);
            params.set('page', '1');

            return params.toString();
        });
    }

    const headers = [
        {
            label: <FormattedMessage id='picking.order.list.table.header.order' />,
            align: "left",
            sortable: true,
            sortDirection,
            onSort: handleSortByCreatedAt
        },
        { label: <FormattedMessage id='picking.order.list.table.header.customer' />, align: "left" },
        { label: <FormattedMessage id='oms.order.list.table.header.zoneAndType' />, align: "left" },
        { label: <FormattedMessage id='picking.order.list.table.header.total' />, align: "left", tooltipId: 'oms.order.list.table.header.total.tooltip' },
        { label: <FormattedMessage id='picking.order.show.table.header.status' />, align: "left" },
        { label: <FormattedMessage id='oms.order.list.table.header.tracking' />, align: "left" },
        ...(isRetailer ? [] : [{ label: <FormattedMessage id='picking.order.list.table.header.affectedTo' />, align: "left" }]),
        { label: <FormattedMessage id='oms.order.list.table.header.message' />, align: "center" },
    ];

    return (
        <>
            <Box sx={{ overflowX: "auto", marginTop: '16px' }}>
                <Table size="small" sx={{ minWidth: 500, borderCollapse: "separate", borderSpacing: 0 }}>
                    <TableHead>
                        <TableHeader headers={headers} />
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <OrderRowsSkeleton />
                        ) : retrieved.length === 0 ? (
                            <TableRow>
                                <TableCell
                                    colSpan={headers.length}
                                    sx={{
                                        padding: '0 !important',
                                        borderLeft: `1px solid ${greyishBlue100}`,
                                        borderRight: `1px solid ${greyishBlue100}`,
                                        borderBottom: `1px solid ${greyishBlue100}`,
                                    }}
                                >
                                    {hasActiveSearchOrFilters ? (
                                        <ListingEmptyState
                                            titleId="oms.order.list.noResult.title"
                                            descriptionId="oms.order.list.noResult.description"
                                            ctaLabelId="oms.order.list.noResult.cta"
                                            ctaVariant="secondary"
                                            onCtaClick={handleResetSearchAndFilters}
                                        />
                                    ) : (
                                        <ListingEmptyState
                                            titleId="oms.order.list.emptyPeriod.title"
                                            descriptionId="oms.order.list.emptyPeriod.description"
                                        />
                                    )}
                                </TableCell>
                            </TableRow>
                        ) : (
                            retrieved.map((order) => (
                                <OrderRow key={order.id} order={order} />
                            ))
                        )}
                    </TableBody>
                </Table>
            </Box>

            <Pagination
                key={paginationKey}
                pageCount={pageCount}
                currentPage={currentPage}
                handlePageChange={handlePageChange}
                showPageCount={false}
            />
        </>
    );
}

export default OrderTable;
